import {
  Button,
  Combobox,
  Option,
  Field,
  makeStyles,
  Subtitle2,
  Spinner,
  InfoLabel,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Link,
} from "@fluentui/react-components";
import React, { useEffect, useRef, useState } from "react";
import { useAddInStore, useTokenStore } from "../../../hooks";
import { supabaseApi } from "../../../services";
import { Space } from "../../../models";
import { createCADchatMeetingStageId, logger } from "../../../utils";
import { useOfficeItem } from "../../../providers";
import { SpaceAttendeeSelectList } from "../common";

const useStyles = makeStyles({
  message_bar: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
  },
  title: {
    paddingLeft: "15px",
    paddingRight: "15px",
    marginBottom: "10px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "10px 15px",
  },
  section_centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "10px 15px",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignContent: "center",
    gap: "10px",
    padding: "10px 15px",
  },
  member_list: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "15px",
  },
  select: {
    width: "100%",
  },
  buttons: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px 15px",
    gap: "10px",
  },
  button: {
    width: "100%",
  },
});

export const AddToExistingSpace = () => {
  const styles = useStyles();

  // indicates the current event and function to update attendees
  const { event, isLoading, officeService } = useOfficeItem();

  // indicates the current user is authenticated
  const isAuthenticated = useTokenStore((state) => state.isAuthenticated);

  // indicates the currently logged in user organization id
  const user = useAddInStore((state) => state.user);

  // used to set the meeting in the state
  const meeting = useAddInStore((state) => state.meeting);

  // indicates the selected organization id
  const selectedOrganizationId = useAddInStore((state) => state.selectedOrganizationId);

  // indicates the selected space id
  const selectedSpaceId = useAddInStore((state) => state.selectedSpaceId);

  // used to update the selected space id
  const setSelectedSpaceId = useAddInStore((state) => state.setSelectedSpaceId);

  // used to update the current view
  const setCurrentView = useAddInStore((state) => state.setCurrentView);

  // indicates the list of sharable spaces
  const [spaces, setSpaces] = useState<Space[]>([]);

  // indicates that we are fetching new spaces
  const [isLoadingSpaces, setIsLoadingSpaces] = useState<boolean>(true);

  // indicates that we are creating the space meeting
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  // prevents rapid repeated clicks before React commits disabled state
  const stageInProgressRef = useRef<boolean>(false);

  // indicates the current compose item already has a pending CADchat meeting marker
  const [isMeetingStaged, setIsMeetingStaged] = useState<boolean>(false);

  // indicates that we are removing a previously staged CADchat meeting
  const [removeInProgress, setRemoveInProgress] = useState<boolean>(false);

  // un-stages the pending CADchat meeting so the user can adjust or cancel it
  const handleRemovePendingMeeting = async () => {
    try {
      setRemoveInProgress(true);
      await officeService.removePendingCADchatMeetingDetails();
      setIsMeetingStaged(false);
    } catch (error) {
      logger.error("Error removing pending CADchat meeting: ", error);
    } finally {
      setRemoveInProgress(false);
    }
  };

  // used to update the selected space id
  const handleSpaceCreation = async () => {
    try {
      if (
        !selectedOrganizationId ||
        !selectedSpaceId ||
        !event?.start ||
        !event?.end ||
        saveInProgress ||
        meeting ||
        isMeetingStaged ||
        stageInProgressRef.current
      ) {
        return;
      }

      stageInProgressRef.current = true;
      setSaveInProgress(true);

      const selectedSpace = spaces.find((x) => x.id === selectedSpaceId);
      const meetingTitle =
        event?.subject && event?.subject !== ""
          ? event?.subject
          : selectedSpace?.name || `Workspace Meeting ${new Date().toLocaleDateString()}`;

      if (!event?.subject || event.subject === "") {
        await officeService.setSubject(meetingTitle);
      }

      await officeService.setLocation("CADchat Meeting");
      await officeService.addPendingCADchatMeetingDetails({
        stageId: createCADchatMeetingStageId(),
        mode: "existing_space",
        organizationId: selectedOrganizationId,
        spaceId: selectedSpaceId,
        spaceName: selectedSpace?.name ?? undefined,
        title: meetingTitle,
        organizerEmail: user?.login_email,
      });

      setIsMeetingStaged(true);
    } catch (error) {
      logger.error("Error creating space meeting: ", error);
    } finally {
      stageInProgressRef.current = false;
      setSaveInProgress(false);
    }
  };

  useEffect(() => {
    if (!selectedSpaceId) return;

    // used to test for the default value outlook will set for some events
    const newEventRegex = /new event/i;

    // find the selected space information
    const selectedSpace = spaces.find((x) => x.id === selectedSpaceId);

    // if we can't find the selected space or the space name is already set in the event subject,
    // we don't need to update the event subject.
    // This prevents unnecessary updates to the event subject.
    if (
      !selectedSpace ||
      (event?.subject !== "" && !newEventRegex.test(event?.subject)) ||
      !selectedSpace?.name ||
      selectedSpace?.name === event?.subject
    ) {
      return;
    }

    // if we made it this far go ahead and set the subject to the same as the space name.
    officeService.setSubject(selectedSpace.name);
  }, [selectedSpaceId, event?.id]);

  useEffect(() => {
    officeService
      .hasPendingCADchatMeetingDetails()
      .then(setIsMeetingStaged)
      .catch((error) => logger.error("Error checking pending CADchat meeting details:", error));
  }, [officeService, event?.body]);

  useEffect(() => {
    const getSpaces = async () => {
      try {
        const response = await supabaseApi.getShareableSpaces(selectedOrganizationId);
        if (response) {
          setSpaces(response);
          if (!response.find((x) => x.id === selectedSpaceId)) {
            // if the selected space is in the list of spaces, we can keep it selected
            setSelectedSpaceId(undefined);
          }
        }
      } catch (err) {
        logger.error("Error fetching spaces", err);
        setSpaces([]);
      } finally {
        setIsLoadingSpaces(false);
      }
    };

    if (isAuthenticated && selectedOrganizationId) {
      getSpaces();
    } else if (isAuthenticated && !selectedOrganizationId) {
      setSpaces([]);
      setIsLoadingSpaces(false);
    }
  }, [isAuthenticated, selectedOrganizationId]);

  return (
    <>
      {!isLoadingSpaces && spaces.length === 0 ? (
        <div className={styles.message_bar}>
          <MessageBar key="no-space-warning" intent="warning">
            <MessageBarBody>
              <MessageBarTitle>No available spaces</MessageBarTitle>
              <br />
              Please use the{" "}
              <Link onClick={() => setCurrentView("create_space")}>create space section</Link> to
              add a meeting to this event. {selectedSpaceId ?? "selected space id is undefined."}
            </MessageBarBody>
          </MessageBar>
        </div>
      ) : null}

      <Subtitle2 className={styles.title}>Schedule in Existing Space</Subtitle2>
      <div className={styles.section_centered}>
        <Field
          label={"Space"}
          id="space-select"
          hint={"Select an existing space to add the meeting to."}
          className={styles.select}
        >
          <Combobox
            id="space-select"
            className={styles.select}
            value={spaces.find((x) => x.id === selectedSpaceId)?.name}
            onOptionSelect={(_e, data) => {
              setSelectedSpaceId(data.optionValue ?? undefined);
            }}
            disabled={spaces.length === 0}
            placeholder="Select an existing space"
          >
            {spaces.map((space) => (
              <Option key={space.id} value={space.id} text={space.name}>
                {space.name}
              </Option>
            ))}
          </Combobox>
        </Field>
      </div>

      <div className={styles.member_list}>
        <SpaceAttendeeSelectList spaceId={selectedSpaceId} meetingCreatedBy={user?.id} />
      </div>

      <div className={styles.section}>
        <InfoLabel
          size="medium"
          weight="semibold"
          info={`Once you click the "Add CADchat Meeting" button, this add-in will add CADchat meeting information to the calendar event. 
              You will still need to complete the creation of the CADchat meeting and calendar event from Outlook by clicking the "Send" button.`}
        >
          Send Invitations
        </InfoLabel>
      </div>

      <div className={styles.buttons}>
        <Button
          appearance="primary"
          className={styles.button}
          disabled={!selectedSpaceId || isLoading || saveInProgress || isMeetingStaged || !!meeting}
          icon={saveInProgress ? <Spinner size="tiny" /> : undefined}
          onClick={async () => {
            await handleSpaceCreation();
          }}
        >
          {isMeetingStaged ? "CADchat Meeting Added" : "Add CADchat Meeting"}
        </Button>
        {isMeetingStaged && !meeting ? (
          <Button
            appearance="secondary"
            className={styles.button}
            disabled={removeInProgress}
            icon={removeInProgress ? <Spinner size="tiny" /> : undefined}
            onClick={() => handleRemovePendingMeeting()}
          >
            Remove CADchat Meeting
          </Button>
        ) : null}
      </div>
    </>
  );
};
